import axios from "axios";

import { currentIdToken, functionBaseUrl } from "../auth/firebase";
import { refreshToken } from "./brightcoveRequest";
import { parseEqpField } from "./customField";
import { t } from "../../i18n/language";

/**
 * Equal Play plugin options as they live in a Brightcove player configuration.
 *
 *   config.plugins.find(p => p.name === "equalplay").options
 *
 * Until now the only way to set these was pasting JSON into Brightcove Studio.
 * This module is the read/write layer behind the options UI.
 *
 * Nothing here goes through Redux: Brightcove is the source of truth for player
 * configuration and the editor fetches what it needs.
 */

/** The plugin's own defaults, from esm-plugin/src/js/defaultOptions.js @1.1.38.
 *  An option absent from a player's config behaves as the value below, so the
 *  editor shows these rather than blanks. */
const PLUGIN_DEFAULTS = {
  signPos: [70, 35, 25],
  signSize: 35,
  signBgColor: "rgba(40,40,40,0.7)",
  signLabel: false,
  hideAudioButton: true,
  hideDescriptionsButton: true,
  hideTitle: false,
  hideTooltips: false,
  textButtons: false,
  verboseLabels: false,
  showIcons: false,
  useZoomHandler: true,
};

/**
 * The plugin build this editor was written against.
 *
 * Options are read by whichever build the player has, so a player on an older
 * build silently ignores options it doesn't know. Per-option "introduced in"
 * versions would be better, but they aren't recoverable - the plugin repo has a
 * single commit touching defaultOptions.js - so the check is deliberately
 * coarse: warn when the installed build is older than this, and never hide an
 * option that is already set on the player.
 */
const EDITOR_BUILT_FOR = "1.1.38";

/**
 * The editable options, in display order.
 *
 * `kind` drives the control. Keep this list in step with PLUGIN_OPTIONS in the
 * `setPluginOptions` Cloud Function - that is the allow-list that actually
 * enforces what may be written, and it will refuse a key that only exists here.
 *
 * `debug` and `transcriptContainer` are deliberately absent: both are developer
 * settings, and transcriptContainer is an embed-level concern (which DOM node
 * the transcript renders into) rather than a per-player preference.
 */
const PLAYER_OPTION_FIELDS = [
  {
    key: "signBgColor",
    kind: "color",
    group: "sign",
    label: () => t("Sign language background"),
    help: () =>
      t("Viewers can change this themselves — this sets the starting value."),
  },
  {
    key: "signSize",
    kind: "size",
    group: "sign",
    label: () => t("Sign language size"),
    help: () =>
      t("Viewers can change this themselves — this sets the starting value."),
  },
  {
    key: "signPos",
    kind: "position",
    group: "sign",
    label: () => t("Sign language position"),
    help: () =>
      t(
        "Drag and resize the interpreter in the preview, or type the values here. A video with its own position set overrides this."
      ),
  },
  {
    key: "hideAudioButton",
    kind: "boolean",
    group: "controls",
    label: () => t("Hide the audio track button"),
    help: () =>
      t(
        "Brightcove's own button, in the player's lower control bar. Equal Play puts its own audio control in its bar, so this is normally hidden."
      ),
  },
  {
    key: "hideDescriptionsButton",
    kind: "boolean",
    group: "controls",
    label: () => t("Hide the audio description button"),
    help: () =>
      t(
        "Brightcove's own button, in the player's lower control bar. Equal Play puts its own description control in its bar, so this is normally hidden."
      ),
  },
  {
    key: "hideTitle",
    kind: "boolean",
    group: "controls",
    label: () => t("Hide the video title"),
    help: () =>
      t("Hides the title Brightcove shows across the top of the player."),
  },
  {
    key: "hideTooltips",
    kind: "boolean",
    group: "controls",
    label: () => t("Hide tooltips"),
    help: () =>
      t("Turns off the explanatory tooltips on Equal Play's own buttons."),
  },
  {
    key: "textButtons",
    kind: "boolean",
    group: "controls",
    label: () => t("Use text buttons instead of icons"),
    help: () =>
      t("Labels Equal Play's buttons with words rather than symbols."),
  },
  {
    key: "showIcons",
    kind: "boolean",
    group: "controls",
    label: () => t("Show icons on text buttons"),
    help: () => t("Keeps the symbol alongside the text."),
    dependsOn: "textButtons",
  },
  {
    key: "verboseLabels",
    kind: "boolean",
    group: "controls",
    label: () => t("Write out on and off in button labels"),
    help: () =>
      t(
        "Adds the current state to each label, for example \"Sign language: off\"."
      ),
    dependsOn: "textButtons",
  },
  {
    key: "signLabel",
    kind: "label",
    group: "controls",
    label: () => t("Sign language button label"),
    help: () =>
      t(
        "Replaces the text on the sign language button, for example \"BSL\". Leave empty for the translated default."
      ),
    dependsOn: "textButtons",
  },
  {
    key: "useZoomHandler",
    kind: "boolean",
    group: "controls",
    label: () => t("Keep controls visible when the page is zoomed"),
    help: () =>
      t(
        "Scales Equal Play's controls with the browser's zoom so they stay usable."
      ),
  },
];

const EDITABLE_KEYS = PLAYER_OPTION_FIELDS.map((field) => field.key);

const pluginAssetUrls = (plugin) => [
  ...(plugin?.scripts || []),
  ...(plugin?.stylesheets || []),
];

/** Pull the installed plugin version out of a player's asset URLs, e.g.
 *  ".../equalplay_1.1.38.js" -> "1.1.38". Returns null when it can't tell —
 *  files uploaded by hand through Studio get an opaque generated name. */
const installedPluginVersion = (plugin) => {
  for (const url of pluginAssetUrls(plugin)) {
    const match = /equalplay[_-]v?(\d+\.\d+\.\d+)/i.exec(url);
    if (match) return match[1];
  }
  return null;
};

/** -1, 0 or 1, comparing dotted numeric versions. Unparseable parts sort as 0. */
const compareVersions = (a, b) => {
  const left = String(a)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  const right = String(b)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
};

/** True when the installed build predates the one this editor knows about, so
 *  some options may be ignored by the player. Unknown version => no warning;
 *  we can't tell, and crying wolf on every hand-uploaded plugin is worse. */
const isOlderThanEditor = (version) =>
  !!version && compareVersions(version, EDITOR_BUILT_FOR) < 0;

/** The equalplay entry in a player's master-branch configuration, or null. */
const getEqualplayPlugin = (player) => {
  const plugins = player?.branches?.master?.configuration?.plugins;
  if (!Array.isArray(plugins)) return null;
  return plugins.find((plugin) => plugin.name === "equalplay") || null;
};

/**
 * The options to show for a player: the plugin's defaults, overlaid with
 * whatever the player actually has set.
 */
const effectiveOptions = (plugin) => ({
  ...PLUGIN_DEFAULTS,
  ...(plugin?.options || {}),
});

/**
 * Only the keys that differ from what the player already has — the payload the
 * save sends, and what the confirm step lists.
 *
 * Comparison is by JSON because signPos is an array; every other value is a
 * primitive, so this is exact rather than merely close enough.
 */
const changedOptions = (plugin, edited) => {
  const current = effectiveOptions(plugin);
  const changed = {};

  for (const key of EDITABLE_KEYS) {
    if (!(key in edited)) continue;
    if (JSON.stringify(edited[key]) !== JSON.stringify(current[key])) {
      changed[key] = edited[key];
    }
  }

  return changed;
};

/**
 * Every player on the account that has the Equal Play plugin installed.
 *
 * Reads players.api directly with the account's Brightcove token, the same way
 * the rest of the dashboard talks to Brightcove. Unlike the Delivery System
 * (asset hosting) this endpoint works on trial accounts, so the options UI is
 * available everywhere the plugin is.
 *
 * @param {string} accountId
 * @returns {Promise<Array<{id, name, plugin, options, version}>>}
 */
const listPlayersWithPlugin = async (accountId) => {
  const token = await refreshToken(accountId);

  const response = await axios({
    method: "GET",
    url: `https://players.api.brightcove.com/v2/accounts/${accountId}/players`,
    headers: { Authorization: `Bearer ${token.currentToken}` },
  });

  const players = response.data?.items || [];

  return players
    .map((player) => {
      const plugin = getEqualplayPlugin(player);
      if (!plugin) return null;
      return {
        id: player.id,
        name: player.name,
        plugin,
        options: effectiveOptions(plugin),
        version: installedPluginVersion(plugin),
      };
    })
    .filter(Boolean);
};

/**
 * Write plugin options to one player.
 *
 * Goes through the `setPluginOptions` Cloud Function rather than Brightcove
 * directly: the function verifies the caller's Firebase ID token, checks the
 * account against the server's copy of the user document, validates every
 * option, and touches nothing in the configuration but the plugin's `options`.
 * The browser never sends a player configuration.
 *
 * The write is live — Brightcove players have no draft state, and the function
 * publishes straight after the PATCH.
 *
 * @param {object} params
 * @param {string} params.accountId
 * @param {string} params.playerId
 * @param {object} params.options - only the options to change
 * @returns {Promise<{options: object}>} the player's full options after the write
 */
const savePlayerOptions = async ({ accountId, playerId, options }) => {
  const idToken = await currentIdToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${functionBaseUrl}setPluginOptions`,
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      data: { account_id: accountId, player_id: playerId, options },
    });

    return response.data;
  } catch (err) {
    // The function answers with a plain-text reason ("You do not have access to
    // this Brightcove account", "This player does not have the Equal Play
    // plugin installed"). Axios hides those behind "Request failed with status
    // code 403", so lift the useful message out before it reaches the UI.
    const detail = err?.response?.data;
    if (typeof detail === "string" && detail.length > 0) {
      throw new Error(detail);
    }
    throw err;
  }
};

/**
 * A few recent videos from the account, for the preview's video picker.
 *
 * Sign language videos are excluded. Those are the interpreter footage the
 * dashboard uploads as its own BC videos, not something anyone would preview a
 * player with, and on a client account they are numerous enough to bury the
 * real ones. They are filtered twice over:
 *
 *   * `-tags:equalplay-alpha` in the query, the same exclusion `bcCMSVideos`
 *     uses for the main video list — the tag is applied when the placeholder
 *     is created in SignLanguage.js.
 *   * `is_sign` on the parsed `equalplay` field, which catches any that
 *     predate the tag or arrived another way. `bcDeleteVideo` treats the two
 *     as independent signals for the same reason.
 *
 * Only videos that can actually play are listed. The CMS API happily returns
 * inactive videos and ones still being ingested, but the Playback API the
 * player uses answers 404 for both, so offering them produces a preview that
 * cannot load. `state === "ACTIVE"` is the same gate TableRow.js puts in front
 * of its play button; `complete` additionally rules out a video whose
 * renditions have not finished.
 *
 * Deliberately not `bcCMSVideos` itself: that one also applies the user's tag
 * filters and dispatches into the Redux `videos` slice, neither of which this
 * needs.
 */
const listRecentVideos = async (accountId, limit = 20) => {
  const token = await refreshToken(accountId);

  const response = await axios({
    method: "GET",
    url: `https://cms.api.brightcove.com/v1/accounts/${accountId}/videos?limit=${limit}&sort=-updated_at&q=${encodeURIComponent(
      "+state:ACTIVE -tags:equalplay-alpha"
    )}`,
    headers: { Authorization: `Bearer ${token.currentToken}` },
  });

  return (response.data || [])
    .map((video) => {
      const eqpField = parseEqpField(video, "player options preview");
      const signTracks = Array.isArray(eqpField?.sign) ? eqpField.sign : [];

      return {
        id: video.id,
        name: video.name,
        isSignVideo: !!eqpField?.is_sign,
        // `complete` is absent on some older videos, so only an explicit
        // false counts as "not ready".
        isPlayable: video.state === "ACTIVE" && video.complete !== false,
        // The preview is only worth looking at on a video that actually has an
        // approved interpreter — otherwise the sign window never appears and the
        // sign options look broken.
        hasApprovedSign: signTracks.some(
          (track) => track.status === "approved"
        ),
      };
    })
    .filter((video) => !video.isSignVideo && video.isPlayable);
};

export {
  PLUGIN_DEFAULTS,
  PLAYER_OPTION_FIELDS,
  EDITABLE_KEYS,
  EDITOR_BUILT_FOR,
  changedOptions,
  compareVersions,
  effectiveOptions,
  getEqualplayPlugin,
  installedPluginVersion,
  isOlderThanEditor,
  listPlayersWithPlugin,
  listRecentVideos,
  pluginAssetUrls,
  savePlayerOptions,
};
